<script setup lang="ts">
import { AxisItem } from "@/modules/explorer/components/AxisConfiguration/Selection.vue";
import DocumentVerticalDTO = App.Data.DocumentVerticalDTO;
import { computed, toRefs } from "vue";
import { useSegmentStore } from "@/modules/explorer/SegmentStore";
import DocumentDimensionDTO = App.Data.DocumentDimensionDTO;
import { verticalHasSegmentOption } from "@/modules/explorer/PropertyStore";

const props = withDefaults(
	defineProps<{
		item: AxisItem;
		showDescriptionAsText?: boolean;
		isAvailable?: boolean;
		modelValue: Object;
	}>(),
	{ showDescriptionAsText: false, isAvailable: true }
);

const { modelValue } = toRefs(props);

const emit = defineEmits(["update:modelValue"]);

const isChecked = computed(() =>
	(modelValue.value as { id: number }[] | null)?.some(
		(selected) => selected?.id === props.item.id
	)
);

const checkboxValue = computed({
	get() {
		return modelValue.value;
	},
	set(value) {
		emit("update:modelValue", value);
	},
});

const stripLocalizedFields = (
	inputObj: AxisItem
): DocumentDimensionDTO | DocumentVerticalDTO => {
	// if hierarchy, set default hierarchy id
	if (inputObj.type === "hierarchy") {
		inputObj["hierarchy_definition_id"] =
			inputObj?.hierarchy_definitions.find(
				(hierarchyDefinition) => hierarchyDefinition.is_default
			)?.id ?? null;
	}

	// if bucketed, set default bucket id
	if (inputObj.type === "bucketed") {
		inputObj["dimension_bucket_id"] =
			inputObj?.dimension_buckets.find(
				(bucketDefinition) => bucketDefinition.is_default
			)?.id ?? null;
	}

	// if bucketed, set default segment id
	if (
		inputObj.type === "segment" ||
		verticalHasSegmentOption(inputObj.name_clean)
	) {
		inputObj["segment_id"] = useSegmentStore().segments[0]?.id ?? null;
	}

	delete inputObj["localizedDescription"];
	delete inputObj["localizedTitle"];
	delete inputObj["hierarchy_definitions"];
	delete inputObj["dimension_buckets"];

	return inputObj;
};
</script>

<template>
	<div class="axis-item" :class="{ 'axis-item--selected': isChecked }">
		<v-checkbox
			v-model="checkboxValue"
			:value="stripLocalizedFields({ ...props.item })"
			:value-comparator="(a, b) => a?.id === b?.id"
			:disabled="!isAvailable"
			:multiple="true"
			:hide-details="true"
			density="compact"
		>
			<template v-slot:label>
				<v-tooltip :text="props.item.localizedDescription" location="end">
					<template v-slot:activator="slot">
						<span v-bind="slot.props" class="axis-item__label">
							<span class="axis-item__title">
								{{ props.item.localizedTitle }}
							</span>
							<span
								v-if="props.showDescriptionAsText"
								class="axis-item__description text-caption"
							>
								{{ props.item.localizedDescription }}
							</span>
						</span>
					</template>
				</v-tooltip>
			</template>
		</v-checkbox>
		<div v-if="$slots.specification" class="axis-item__specification">
			<slot name="specification" />
		</div>
	</div>
</template>

<style scoped lang="scss">
/* Mirrors SelectionItem: one-line rows with the specification on the trailing
   edge, so single- and multi-select lists read identically. */
.axis-item {
	display: flex;
	align-items: center;
	gap: 0.5rem;
	min-width: 0;
	border-radius: 4px;

	&:hover {
		background: rgb(var(--color-secondary));
	}
}

.axis-item__label {
	display: flex;
	align-items: center;
	gap: 0.375rem;
	min-width: 0;
}

.axis-item__title {
	flex: 0 1 auto;
	overflow: hidden;
	text-overflow: ellipsis;
	white-space: nowrap;
}

.axis-item--selected .axis-item__title {
	font-weight: 600;
}

.axis-item__description {
	/* A zero basis keeps the title at its natural width and spends whatever is
	   left on the description, so the title is the last thing to be clipped. */
	flex: 1 1 0;
	min-width: 0;
	overflow: hidden;
	text-overflow: ellipsis;
	white-space: nowrap;
	color: rgb(var(--color-on-background));
}

.axis-item__specification {
	flex: 0 0 auto;
	margin-inline-start: auto;
	padding-inline-end: 0.25rem;
}
</style>
